<template>
  <modal-wrap
    :id="id"
    ref="packingSlips"
    size="lg"
    body-class="packing-slips-modal"
    :header-class="'packing-slips-header'"
    :hide-footer="true"
    :name="modalName"
  >
    <template #modal-title> Packing Slips </template>
    <template #default>
      <div>
        <div class="row mt-3 mb-3">
          <ClientSelect v-if="isStaff" table-name="packing-slips-list" @change-client="setClient" />
        </div>
      </div>
      <div id="packingSlips">
        <div v-for="(packingSlip, i) in packingSlipsItems" :key="packingSlip.id + '_' + i" role="tablist">
          <SinglePackingSlip :packing-slip="packingSlip" :index="packingSlip.id" />
        </div>
        <PackingSlipPagination
          class="mt-3 p-1 VueTables__pagination"
          :records="totalPackages"
          :per-page="perPage"
          :value="page"
          @input="setPage"
        />
        <div v-if="loading" class="d-flex justify-content-center pt-5 pb-5">
          <b-spinner class="mt-5" variant="success" />
        </div>
        <div v-if="!loading && packingSlipsItems.length === 0" class="d-flex justify-content-center pt-5 pb-5">
          {{ !isStaff ? "You Don't Have Any Packing Slips" : 'There are not any packing slips.' }}
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="row w-100 m-0">
        <div class="col-7 p-0 my-auto">
          <div class="row">
            <div class="col-6 package-info my-auto no-wrap">
              Total weight: <span>{{ totalWeight }}</span>
            </div>
            <div class="col-6 package-info my-auto no-wrap">
              Total Packing Slips: <span>{{ totalPackages }}</span>
            </div>
          </div>
        </div>
        <Continue :enable-continue-button="enableContinueButton" :from-previous-packing-slip="true" />
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, onMounted, onBeforeMount } from 'vue'
import { PACKING_SLIPS } from '@/const/ModalTypes'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { usePackingSlipStore } from '@/store/packingSlip'
import Continue from '@/views/Components/PackingSlip/Continue.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import ClientSelect from '@/views/Components/PackingSlip/ClientSelect.vue'
import SinglePackingSlip from '@/views/Components/PackingSlip/SinglePackingSlip.vue'
import PackingSlipPagination from '@/views/Components/Elements/Table/PackingSlipPagination.vue'

export default defineComponent({
  name: 'PackingSlipsModal',
  components: {
    BSpinner,
    Continue,
    ModalWrap,
    ClientSelect,
    PackingSlipPagination,
    SinglePackingSlip,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const accountStore = useAccountStore()
    const packingSlipStore = usePackingSlipStore()
    const loading = ref(true)
    const page = ref(1)
    const perPage = ref(10)
    const modalName = PACKING_SLIPS

    const checkedItems = computed(() => packingSlipStore.checkedItems)
    const packingSlipsItems = computed(() => packingSlipStore.packingSlips)
    const totalPackages = computed(() => packingSlipStore.total)
    const totalWeight = computed(() => packingSlipStore.totalWeight)
    const isStaff = computed(() => accountStore.isStaff)

    const enableContinueButton = computed(() => checkedItems.value.length > 0 && packingSlipsItems.value.length !== 0)

    const setPage = (newPage: number) => {
      page.value = newPage
      packingSlipStore.getPackingSlips({ page: newPage })
    }
    const setClient = (companyId: number) => {
      packingSlipStore.setCompanyId(String(companyId))
      packingSlipStore.getPackingSlips({
        page: page.value,
        limit: perPage.value,
        companyId,
      })
    }

    onMounted(async () => {
      openModal(modalName)
      loading.value = false
      await packingSlipStore.getPackingSlips()
      const payload: { page: number; limit: number; companyId: number | string | null } = {
        page: page.value,
        limit: perPage.value,
        companyId: null,
      }
      if (!isStaff.value) {
        payload.companyId = accountStore.companyId
      }
      await packingSlipStore.getPackingSlips(payload)
    })

    onBeforeMount(() => {
      packingSlipStore.clearCheckedItems()
      packingSlipStore.setCompanyId(null)
    })

    return {
      page,
      perPage,
      loading,
      modalName,
      enableContinueButton,
      packingSlipsItems,
      totalPackages,
      totalWeight,
      isStaff,
      setClient,
      setPage,
    }
  },
})
</script>
